import React, { useState, useEffect } from "react";
import {
	Table,
	Tag,
	Row,
	Col,
	Card,
	Input,
	Select,
	Checkbox,
	Menu,
} from "antd";
import { MailOutlined } from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import Header from "../../components/header";
import EmailLogService from "../../services/emailLog";
import userService from "../../services/user";

const { Search } = Input;
const { Option } = Select;

const EmailLogs = () => {
	const [emailLogs, setEmailLogs] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalDataCount, setTotalDataCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(15);
	const [searchText, setSearchText] = useState("");
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [userOptions, setUserOptions] = useState([]);

	useEffect(() => {
		// Only run on mount to get users
		getUsers();
	}, []);

	useEffect(() => {
		// Only run on mount, not on search/filter change
		fetchEmailLogs(currentPage, searchText, selectedUsers);
		// eslint-disable-next-line
	}, []);

	const getUsers = async () => {
		try {
			const response = await userService.getAllUsersList();
			if (Array.isArray(response.data)) {
				setUserOptions(response.data);
			} else if (Array.isArray(response)) {
				setUserOptions(response);
			}
		} catch (error) {
			console.error("Failed to fetch users:", error.message);
		}
	};

	const fetchEmailLogs = async (page = 1, search = "", users = []) => {
		setLoading(true);
		try {
			const response = await EmailLogService.getEmailLogs(page, search, users);
			const logs = Array.isArray(response.data.data) ? response.data.data : [];
			setEmailLogs(logs);
			setCurrentPage(page);
			setTotalDataCount(response.data.total);
			setItemsPerPage(response.data.per_page);
		} catch (error) {
			console.error("Failed to fetch email logs:", error.message);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
		setCurrentPage(1);
		const selectedEmails = userOptions
			.filter((user) => selectedUsers.includes(user.id))
			.map((user) => user.email);
		fetchEmailLogs(1, value, selectedEmails);
	};

	const handleUserChange = (value) => {
		setSelectedUsers(value);
		setCurrentPage(1);
		// Map selected user IDs to emails for backend
		const selectedEmails = userOptions
			.filter((user) => value.includes(user.id))
			.map((user) => user.email);
		fetchEmailLogs(1, searchText, selectedEmails);
	};

	const handlePageChange = (page, pageSize) => {
		setCurrentPage(page);
		const selectedEmails = userOptions
			.filter((user) => selectedUsers.includes(user.id))
			.map((user) => user.email);
		fetchEmailLogs(page, searchText, selectedEmails);
	};

	const columns = [
		{
			title: "Recipient",
			dataIndex: "recipient",
			key: "recipient",
		},
		{
			title: "Subject",
			dataIndex: "subject",
			key: "subject",
		},
		{
			title: "Body",
			dataIndex: "body",
			key: "body",
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: (status) => {
				let color = "geekblue";
				if (status === "delivered") color = "green";
				else if (status === "failed") color = "volcano";
				return <Tag color={color}>{status.toUpperCase()}</Tag>;
			},
		},
		{
			title: "Error Message",
			dataIndex: "error_message",
			key: "error_message",
		},
		{
			title: "Sent At",
			dataIndex: "created_at",
			key: "created_at",
		},
	];

	return (
		<DefaultLayout>
			<Row gutter={[24, 24]}>
				<Col span={24}>
					<Header icon={<MailOutlined />} title='Email Logs' />
				</Col>
			</Row>
			<Card>
				<Row
					gutter={[24, 24]}
					style={{ marginBottom: 24 }}
					align='middle'
					justify='start'
				>
					<Col xs={24} sm={12} md={8} lg={6} style={{ marginBottom: 12 }}>
						<Search
							placeholder='Search by subject, body, or status'
							onSearch={handleSearch}
							enterButton
							allowClear
							style={{ width: "100%" }}
						/>
					</Col>
					<Col xs={24} sm={12} md={8} lg={6} style={{ marginBottom: 12 }}>
						<Select
							mode='multiple'
							placeholder='Filter by user(s)'
							onChange={handleUserChange}
							style={{ width: "100%" }}
							allowClear
							value={selectedUsers}
							maxTagCount={2}
							showSearch
							optionFilterProp="children"
							filterOption={(input, option) =>
								option.children.toLowerCase().includes(input.toLowerCase())
							}
							maxTagPlaceholder={(omittedValues) =>
								`+${omittedValues.length} more`
							}
						>
							{userOptions.map((user) => (
								<Option key={user.id} value={user.id}>
									{user.name || user.full_name || user.email}
								</Option>
							))}
						</Select>
					</Col>
				</Row>
				<Table
					columns={columns}
					dataSource={emailLogs.map((log) => ({ ...log, key: log.id }))}
					rowKey='id'
					loading={loading}
					pagination={{
						total: totalDataCount,
						current: currentPage,
						pageSize: itemsPerPage,
						onChange: handlePageChange,
					}}
					scroll={{ x: true }}
				/>
			</Card>
		</DefaultLayout>
	);
};

export default EmailLogs;
